import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  getOrgWeightConfig,
  normalizeOrgWeightConfig,
  normalizeOrgWeightOverrides,
  orgWeightValuesEqual,
  ORG_WEIGHT_SCHEME_CUSTOM,
  orgWeightRowKey,
  parentOrgIdForRow,
  parseWeightInput,
  persistedWeightForRow,
  singleOrgWeightPayload,
  weightSchemePayload,
} from './orgWeightsUtils';

const OrgWeightsContext = createContext(null);

export function OrgWeightsProvider({
  organization,
  orgSyncKey,
  onPersist,
  children,
}) {
  const [config, setConfig] = useState(() =>
    normalizeOrgWeightConfig(getOrgWeightConfig(organization))
  );
  const [saving, setSaving] = useState(false);
  const configRef = useRef(config);
  configRef.current = config;

  const persistedWeightsRef = useRef(
    normalizeOrgWeightOverrides(getOrgWeightConfig(organization).weights)
  );

  // Reset from server only when tree structure changes, not on every Redux refresh.
  useEffect(() => {
    if (!organization) return;
    const next = normalizeOrgWeightConfig(getOrgWeightConfig(organization));
    setConfig(next);
    persistedWeightsRef.current = { ...next.weights };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- structure key only
  }, [orgSyncKey]);

  const persist = useCallback(
    async (payload) => {
      if (!organization?.id || !payload) return;
      setSaving(true);
      try {
        await onPersist(organization.id, payload);
      } finally {
        setSaving(false);
      }
    },
    [organization?.id, onPersist]
  );

  const weightValueChanged = useCallback(
    (row, rawValue) => {
      const parsed = parseWeightInput(rawValue);
      const rowKey = orgWeightRowKey(
        organization?.id,
        parentOrgIdForRow(row),
        row
      );
      const persisted =
        rowKey != null
          ? persistedWeightsRef.current[rowKey]
          : persistedWeightForRow(organization, row);
      return !orgWeightValuesEqual(parsed, persisted ?? null);
    },
    [organization]
  );

  const markWeightPersisted = useCallback(
    (row, rawValue) => {
      const rowKey = orgWeightRowKey(
        organization?.id,
        parentOrgIdForRow(row),
        row
      );
      if (!rowKey) return;
      const parsed = parseWeightInput(rawValue);
      if (parsed == null) {
        delete persistedWeightsRef.current[rowKey];
      } else {
        persistedWeightsRef.current[rowKey] = parsed;
      }
    },
    [organization?.id]
  );

  const applyWeightToConfig = useCallback((rowKey, rawValue) => {
    setConfig((prev) => {
      const weights = { ...prev.weights };
      const parsed = parseWeightInput(rawValue);
      if (parsed == null) {
        delete weights[rowKey];
      } else {
        weights[rowKey] = parsed;
      }
      return { ...prev, weights };
    });
  }, []);

  const persistWeightRow = useCallback(
    async (row, rawValue) => {
      const rowKey = orgWeightRowKey(
        organization?.id,
        parentOrgIdForRow(row),
        row
      );
      if (!rowKey) return;

      if (!weightValueChanged(row, rawValue)) {
        return;
      }

      const payload = singleOrgWeightPayload(organization, row, rawValue);
      if (!payload) return;

      const previousWeight = configRef.current.weights[rowKey];
      applyWeightToConfig(rowKey, rawValue);

      try {
        await persist(payload);
        markWeightPersisted(row, rawValue);
      } catch {
        setConfig((prev) => {
          const weights = { ...prev.weights };
          if (previousWeight == null) {
            delete weights[rowKey];
          } else {
            weights[rowKey] = previousWeight;
          }
          return { ...prev, weights };
        });
      }
    },
    [
      organization,
      persist,
      weightValueChanged,
      markWeightPersisted,
      applyWeightToConfig,
    ]
  );

  const onSchemeChange = useCallback(
    (scheme) => {
      const previousScheme = configRef.current.scheme;
      setConfig((prev) => ({ ...prev, scheme }));
      persist(weightSchemePayload(scheme)).catch(() => {
        setConfig((prev) => ({ ...prev, scheme: previousScheme }));
      });
    },
    [persist]
  );

  const onWeightBlur = useCallback(
    (row, rawValue) => {
      persistWeightRow(row, rawValue);
    },
    [persistWeightRow]
  );

  const isCustomScheme = config.scheme === ORG_WEIGHT_SCHEME_CUSTOM;

  const value = useMemo(
    () => ({
      config,
      overrides: normalizeOrgWeightOverrides(config.weights),
      saving,
      isCustomScheme,
      onSchemeChange,
      onWeightBlur,
    }),
    [config, saving, isCustomScheme, onSchemeChange, onWeightBlur]
  );

  return (
    <OrgWeightsContext.Provider value={value}>
      {children}
    </OrgWeightsContext.Provider>
  );
}

export function useOrgWeights() {
  const ctx = useContext(OrgWeightsContext);
  if (!ctx) {
    throw new Error('useOrgWeights must be used within OrgWeightsProvider');
  }
  return ctx;
}
